import { useContext, useMemo } from "react";

import { useInfiniteQuery } from "@tanstack/react-query";
import { OspApiClientContext } from "@features/api/client/OspApiClientProvider";
import { QUERY_KEYS } from "@config/constant";
import {
  ListPaymentRecordRequest,
  PaymentRecordPagination,
} from "../features/api/memberApi";
import { useMemberFeature } from "./useMemberFeature";
import { ProductPlanStatus } from "../components/Member/CurrentPlan/constants";

interface UsePaymentHistoryOptions {
  params: ListPaymentRecordRequest;
  enabled?: boolean;
}
/**
 * 支付历史Hook，使用react-query实现无限加载
 */
export const usePaymentHistory = (options: UsePaymentHistoryOptions) => {
  const { memberApi } = useContext(OspApiClientContext);
  const { data: memberFeatureData } = useMemberFeature();
  const { enabled = true, params } = options;

  // Memoize queryKey to ensure it changes when params change
  const queryKey = useMemo(() => [QUERY_KEYS.PAYMENT_HISTORY], []);

  const {
    data = [],
    isLoading,
    isFetching,
    isPending,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    enabled: !!memberApi && enabled,
    queryKey,
    staleTime: 5 * 60 * 1000, // 5分钟
    queryFn: async (pageParam) => {
      // 使用传入的参数或默认参数
      const requestParams = pageParam?.pageParam || params;
      const res = await memberApi?.payment.listPaymentRecord(
        // @ts-ignore
        requestParams,
      );
      return res?.data?.data as PaymentRecordPagination;
    },
    getNextPageParam: (lastPage, _, lastPageParam) => {
      // 如果有下一页标记，返回新的参数
      if (lastPage?.next_token) {
        // 创建新的参数对象，避免修改原对象
        const nextParams: ListPaymentRecordRequest = {
          ...lastPageParam,
          next_token: lastPage.next_token,
        };
        return nextParams;
      }
      // 没有更多数据时返回 undefined
      return undefined;
    },
    initialPageParam: params,
    select(data) {
      const activeProductIds = memberFeatureData
        ?.filter(
          (item) => item?.status === ProductPlanStatus.ACTIVE && !!item.id,
        )
        ?.map((item) => ({ id: item.id, name: item.name }));
      const mergedData = data?.pages
        ?.map((page) => page?.rows)
        .flat()
        ?.filter((item) => !!item);
      const processedData = mergedData.map((item) => {
        const activeProduct = activeProductIds.find(
          (activeItem) => activeItem?.id === item?.product_id,
        );
        if (activeProduct) {
          return {
            ...item,
            productName: activeProduct?.name,
          };
        }
        return {
          ...item,
          productName:
            (item?.product_id || "").split("_")[0]?.charAt(0).toUpperCase() +
            (item?.product_id || "").split("_")[0]?.slice(1),
        };
      });

      return processedData;
    },
  });

  return {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isPending,
    isFetching,
  };
};
